import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, canWrite, isAdmin } from "@/lib/auth";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ArrowLeft, ShieldAlert, CheckCircle2, AlertTriangle, Info, RefreshCw, Plus, Trash2,
  ClipboardList, FileCheck2, Palette, ScrollText, GitBranch,
} from "lucide-react";
import { AssetPathwayBadge } from "@/components/AssetPathwayBadge";
import { AssetPathwayDialog } from "@/components/AssetPathwayDialog";

// -------------------------------------------------------------------------
// MP12 Module 3 — Track Compliance page.
//
// One consolidated view over the deterministic compliance report + all
// human-editable Module 3 artefacts (compliance tasks, work scope,
// LOC preparation, artwork review) for a single track. Everything is
// reason-first and NEVER framed as legal advice.
// -------------------------------------------------------------------------

const SEV_STYLE = {
  blocker: { bg: "hsl(0 55% 94%)",  fg: "hsl(0 65% 30%)",  border: "hsl(0 55% 72%)",  Icon: AlertTriangle, label: "BLOCKER" },
  warning: { bg: "hsl(40 55% 94%)", fg: "hsl(35 60% 28%)", border: "hsl(40 55% 76%)", Icon: ShieldAlert,  label: "WARNING" },
  note:    { bg: "hsl(210 45% 94%)", fg: "hsl(210 55% 30%)", border: "hsl(210 45% 74%)", Icon: Info,      label: "NOTE" },
};

function SeverityChip({ severity }) {
  const s = SEV_STYLE[severity] || SEV_STYLE.note;
  const Icon = s.Icon;
  return (
    <Badge
      variant="outline"
      className="text-[10px] uppercase tracking-[0.14em] font-semibold inline-flex items-center gap-1"
      style={{ background: s.bg, color: s.fg, borderColor: s.border }}
    >
      <Icon className="w-3 h-3" />
      {s.label}
    </Badge>
  );
}

const LOC_STATUS_LABELS = {
  NOT_STARTED:                    "NOT STARTED",
  INTAKE_INCOMPLETE:              "INTAKE INCOMPLETE",
  PREPARING:                      "PREPARING",
  READY_FOR_HUMAN_REVIEW:         "READY FOR HUMAN REVIEW",
  READY_FOR_EXTERNAL_FILING:      "READY FOR EXTERNAL FILING",
  FILED_EXTERNALLY_EVIDENCE_PENDING: "FILED EXTERNALLY — EVIDENCE PENDING",
  FILING_EVIDENCE_RECORDED:       "FILING EVIDENCE RECORDED",
  ATTORNEY_REVIEW_REQUIRED:       "ATTORNEY REVIEW REQUIRED",
};
const LOC_STATUSES = [
  "NOT_STARTED", "INTAKE_INCOMPLETE", "PREPARING",
  "READY_FOR_HUMAN_REVIEW", "READY_FOR_EXTERNAL_FILING",
  "FILED_EXTERNALLY_EVIDENCE_PENDING", "FILING_EVIDENCE_RECORDED",
  "ATTORNEY_REVIEW_REQUIRED",
];

const ARTWORK_OUTCOMES = [
  { code: "not_started",       label: "Not started" },
  { code: "in_review",         label: "In review" },
  { code: "conditional_ok",    label: "Conditional (internal)" },
  { code: "approved_internal", label: "Internally approved (not legal clearance)" },
  { code: "not_approved",      label: "Not approved" },
  { code: "escalate_attorney", label: "Escalate to attorney" },
];

export default function TrackCompliancePage() {
  const { trackId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const writer = canWrite(user);
  const admin = isAdmin(user);

  const [track, setTrack] = useState(null);
  const [report, setReport] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [workScope, setWorkScope] = useState(null);
  const [locCase, setLocCase] = useState(null);
  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pathwayOpen, setPathwayOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tr, rp, tk, ws, lp, aw] = await Promise.all([
        api.get(`/tracks/${trackId}`).catch(() => ({ data: null })),
        api.get(`/tracks/${trackId}/compliance-report`).catch(() => ({ data: null })),
        api.get(`/tracks/${trackId}/compliance-tasks`).catch(() => ({ data: { rows: [] } })),
        api.get(`/tracks/${trackId}/work-scope`).catch(() => ({ data: { present: false, scope: null } })),
        api.get(`/tracks/${trackId}/loc-prep-case`).catch(() => ({ data: { present: false, case: null } })),
        api.get(`/tracks/${trackId}/artwork-review`).catch(() => ({ data: { present: false, review: null } })),
      ]);
      setTrack(tr.data || null);
      setReport(rp.data || null);
      setTasks(tk.data?.rows || []);
      setWorkScope(ws.data?.scope || null);
      setLocCase(lp.data?.case || null);
      setArtwork(aw.data?.review || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load compliance data.");
    } finally {
      setLoading(false);
    }
  }, [trackId]);

  useEffect(() => { load(); }, [load]);

  if (!writer) {
    return <div className="p-6 text-[13px] text-muted-foreground">Compliance workspace is limited to authorised internal users.</div>;
  }
  if (loading || !track) {
    return <div className="p-6 text-[13px] text-muted-foreground">Loading compliance workspace…</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="track-compliance-page">
      <div className="flex items-center gap-2 flex-wrap">
        <Button asChild variant="ghost" size="sm" data-testid="compliance-back">
          <Link to={`/tracks/${trackId}`}><ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to track</Link>
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-[22px] truncate" data-testid="compliance-track-title">
            Compliance workspace · {track.track_title || "(untitled)"}
          </h1>
          <div className="text-[11px] text-muted-foreground font-mono truncate">{trackId}</div>
        </div>
        <div className="flex items-center gap-2">
          <AssetPathwayBadge value={track.asset_pathway} />
          {admin || user?.role === "catalog_manager" ? (
            <Button size="sm" variant="outline" onClick={() => setPathwayOpen(true)} data-testid="compliance-change-pathway">
              Change pathway
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={load} disabled={busy} data-testid="compliance-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Compliance report */}
      <Card data-testid="compliance-report-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-[15px] flex items-center gap-2">
            <ShieldAlert className="w-4 h-4" style={{ color: "hsl(347 60% 22%)" }} />
            Compliance report
          </CardTitle>
          {report?.disclaimer_text && (
            <p className="text-[11px] italic text-muted-foreground">{report.disclaimer_text}</p>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {report ? (
            <>
              <div className="flex flex-wrap gap-2 text-[11px]">
                <Badge variant="outline" style={{ background: "hsl(0 55% 94%)", color: "hsl(0 65% 30%)", borderColor: "hsl(0 55% 72%)" }} data-testid="compliance-blocker-count">
                  {report.blocker_count} blocker{report.blocker_count === 1 ? "" : "s"}
                </Badge>
                <Badge variant="outline" style={{ background: "hsl(40 55% 94%)", color: "hsl(35 60% 28%)", borderColor: "hsl(40 55% 76%)" }} data-testid="compliance-warning-count">
                  {report.warning_count} warning{report.warning_count === 1 ? "" : "s"}
                </Badge>
                <Badge variant="outline" style={{ background: "hsl(210 45% 94%)", color: "hsl(210 55% 30%)", borderColor: "hsl(210 45% 74%)" }} data-testid="compliance-note-count">
                  {report.note_count} note{report.note_count === 1 ? "" : "s"}
                </Badge>
              </div>

              {report.findings.length === 0 ? (
                <div className="text-[12.5px] text-muted-foreground italic" data-testid="compliance-none">
                  No findings. This does not represent a legal clearance decision.
                </div>
              ) : (
                <ul className="space-y-2" data-testid="compliance-findings">
                  {report.findings.map((f) => (
                    <li key={f.code} className="border rounded p-2.5" data-testid={`compliance-finding-${f.code}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <SeverityChip severity={f.severity} />
                        <Badge variant="outline" className="text-[10px] uppercase font-mono">{f.category}</Badge>
                        <span className="text-[13px] font-medium">{f.title}</span>
                        <span className="ml-auto text-[10.5px] font-mono text-muted-foreground">{f.code}</span>
                      </div>
                      <p className="text-[12px] mt-1"><span className="uppercase tracking-wide text-[10px] text-muted-foreground mr-1.5">Why:</span>{f.reason}</p>
                      {f.condition && (
                        <p className="text-[11px] mt-1 text-muted-foreground font-mono">condition: {f.condition}</p>
                      )}
                      <p className="text-[12px] mt-1"><span className="uppercase tracking-wide text-[10px] text-muted-foreground mr-1.5">Next:</span>{f.suggested_action}</p>
                      <div className="mt-1.5">
                        <CreateTaskFromFinding trackId={trackId} finding={f} onCreated={(t) => setTasks((prev) => [...prev, t])} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <div className="text-[12.5px] text-muted-foreground">Compliance report unavailable.</div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="tasks" className="w-full">
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="tasks" data-testid="compliance-tab-tasks"><ClipboardList className="w-3.5 h-3.5 mr-1.5" />Tasks</TabsTrigger>
          <TabsTrigger value="scope" data-testid="compliance-tab-scope"><ScrollText className="w-3.5 h-3.5 mr-1.5" />Work scope</TabsTrigger>
          <TabsTrigger value="loc" data-testid="compliance-tab-loc"><FileCheck2 className="w-3.5 h-3.5 mr-1.5" />LOC preparation</TabsTrigger>
          <TabsTrigger value="artwork" data-testid="compliance-tab-artwork"><Palette className="w-3.5 h-3.5 mr-1.5" />Artwork review</TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="mt-3">
          <ComplianceTasksTab trackId={trackId} tasks={tasks} setTasks={setTasks} />
        </TabsContent>
        <TabsContent value="scope" className="mt-3">
          <WorkScopeTab trackId={trackId} workScope={workScope} setWorkScope={setWorkScope} admin={admin || user?.role === "catalog_manager"} />
        </TabsContent>
        <TabsContent value="loc" className="mt-3">
          <LocPrepTab trackId={trackId} locCase={locCase} setLocCase={setLocCase} />
        </TabsContent>
        <TabsContent value="artwork" className="mt-3">
          <ArtworkReviewTab trackId={trackId} artwork={artwork} setArtwork={setArtwork} />
        </TabsContent>
      </Tabs>

      <AssetPathwayDialog
        open={pathwayOpen}
        onOpenChange={setPathwayOpen}
        track={track}
        onChanged={(fresh) => { setTrack(fresh); load(); }}
      />
    </div>
  );
}

// ------------------- helpers / sub-tabs -------------------

function CreateTaskFromFinding({ trackId, finding, onCreated }) {
  const [busy, setBusy] = useState(false);
  async function go() {
    setBusy(true);
    try {
      const r = await api.post(`/tracks/${trackId}/compliance-tasks`, {
        title: finding.title,
        detail: finding.suggested_action,
        category: finding.category,
        linked_finding_code: finding.code,
      });
      toast.success("Task created from finding.");
      onCreated && onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create task.");
    } finally { setBusy(false); }
  }
  return (
    <Button size="sm" variant="ghost" onClick={go} disabled={busy} data-testid={`compliance-finding-mktask-${finding.code}`}>
      <Plus className="w-3 h-3 mr-1" /> Create task from finding
    </Button>
  );
}

function ComplianceTasksTab({ trackId, tasks, setTasks }) {
  const [newTitle, setNewTitle] = useState("");

  async function addAdhoc() {
    if (!newTitle.trim()) { toast.error("Task title is required."); return; }
    try {
      const r = await api.post(`/tracks/${trackId}/compliance-tasks`, { title: newTitle.trim() });
      setTasks((prev) => [...prev, r.data]);
      setNewTitle("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add task."); }
  }

  async function patch(tid, fields) {
    try {
      const r = await api.patch(`/tracks/${trackId}/compliance-tasks/${tid}`, fields);
      setTasks((prev) => prev.map((t) => t.id === tid ? r.data : t));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update task."); }
  }
  async function remove(tid) {
    // Compliance tasks are child-of-track resources. Use a compact
    // window.prompt for reason capture — reason is still required
    // and captured in the audit tombstone.
    const reason = window.prompt(
      "Deletion reason (required): Test record | Duplicate | Entered in error | Client request | No longer required | Other: <text>",
      "Entered in error"
    );
    if (!reason) return;
    try {
      const qs = new URLSearchParams();
      qs.set("reason", reason);
      qs.set("force", "true");
      await api.delete(`/tracks/${trackId}/compliance-tasks/${tid}?${qs.toString()}`);
      setTasks((prev) => prev.filter((t) => t.id !== tid));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete task."); }
  }

  return (
    <Card data-testid="tasks-card">
      <CardHeader className="pb-2"><CardTitle className="text-[15px]">Compliance tasks</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <div className="flex gap-2">
          <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Add an ad-hoc task…" className="h-8 text-[12.5px]" data-testid="tasks-new-title" />
          <Button size="sm" onClick={addAdhoc} data-testid="tasks-add"><Plus className="w-3.5 h-3.5 mr-1.5" />Add</Button>
        </div>
        {tasks.length === 0 ? (
          <div className="text-[12.5px] text-muted-foreground italic" data-testid="tasks-empty">
            No compliance tasks yet. Create them from findings above, or add an ad-hoc task.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="tasks-list">
            {tasks.map((t) => (
              <li key={t.id} className="border rounded p-2.5" data-testid={`task-${t.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <Input value={t.title || ""} onChange={(e) => patch(t.id, { title: e.target.value })} className="h-7 text-[12px] font-medium flex-1 min-w-[180px]" data-testid={`task-title-${t.id}`} />
                  <Select value={t.status || "open"} onValueChange={(v) => patch(t.id, { status: v })}>
                    <SelectTrigger className="h-7 w-32 text-[11.5px]" data-testid={`task-status-${t.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In progress</SelectItem>
                      <SelectItem value="blocked">Blocked</SelectItem>
                      <SelectItem value="done">Done</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={t.priority || "normal"} onValueChange={(v) => patch(t.id, { priority: v })}>
                    <SelectTrigger className="h-7 w-24 text-[11.5px]" data-testid={`task-priority-${t.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                  <button type="button" onClick={() => remove(t.id)} className="h-7 w-7 rounded border text-muted-foreground hover:text-[hsl(0_70%_38%)]" aria-label="Delete task" data-testid={`task-remove-${t.id}`}>
                    <Trash2 className="w-3 h-3 mx-auto" />
                  </button>
                </div>
                {(t.linked_finding_code || t.category) && (
                  <div className="text-[10.5px] mt-1.5 text-muted-foreground flex gap-2 flex-wrap">
                    {t.category && <Badge variant="outline" className="text-[10px] uppercase font-mono">{t.category}</Badge>}
                    {t.linked_finding_code && <Badge variant="outline" className="text-[10px] font-mono">{t.linked_finding_code}</Badge>}
                  </div>
                )}
                <Textarea value={t.detail || ""} onChange={(e) => patch(t.id, { detail: e.target.value })} rows={2} className="text-[12px] mt-1.5" placeholder="Detail / context" data-testid={`task-detail-${t.id}`} />
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  <Input value={t.owner || ""} onChange={(e) => patch(t.id, { owner: e.target.value })} placeholder="Owner" className="h-7 text-[11.5px]" data-testid={`task-owner-${t.id}`} />
                  <Input value={t.due_hint || ""} onChange={(e) => patch(t.id, { due_hint: e.target.value })} placeholder="Due hint" className="h-7 text-[11.5px]" data-testid={`task-due-${t.id}`} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function WorkScopeTab({ trackId, workScope, setWorkScope, admin }) {
  const [draft, setDraft] = useState(workScope || null);
  useEffect(() => { setDraft(workScope || null); }, [workScope]);
  const dirty = draft && workScope && JSON.stringify(draft) !== JSON.stringify(workScope);

  async function create() {
    try {
      const r = await api.post(`/tracks/${trackId}/work-scope`, {});
      setWorkScope(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create work scope."); }
  }
  async function save() {
    try {
      const r = await api.patch(`/tracks/${trackId}/work-scope`, {
        summary: draft.summary, objectives: draft.objectives,
        deliverables: draft.deliverables || [],
        assumptions: draft.assumptions, exclusions: draft.exclusions,
        dependencies: draft.dependencies, timeline_note: draft.timeline_note,
        budget_note: draft.budget_note, proposal_title: draft.proposal_title,
        proposal_body: draft.proposal_body,
      });
      setWorkScope(r.data);
      toast.success("Work scope saved.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save work scope."); }
  }
  async function approveToggle() {
    try {
      const approve = (workScope?.approval_status || "draft") !== "internally_approved";
      const r = await api.post(`/tracks/${trackId}/work-scope/approval`, { approve, reason: approve ? "" : "Approval revoked by operator." });
      setWorkScope(r.data);
      toast.success(approve ? "Work scope internally approved." : "Approval revoked.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not change approval."); }
  }

  if (!workScope) {
    return (
      <Card data-testid="workscope-card">
        <CardContent className="p-4 space-y-2">
          <div className="text-[13px]">No work scope yet.</div>
          <p className="text-[11.5px] text-muted-foreground">Work scope is a human-editable draft. Nothing is auto-sent externally. Approval is an internal marker only.</p>
          <Button size="sm" onClick={create} data-testid="workscope-create"><Plus className="w-3.5 h-3.5 mr-1.5" />Start work scope draft</Button>
        </CardContent>
      </Card>
    );
  }

  const st = draft || workScope;
  const patch = (k, v) => setDraft((d) => ({ ...(d || {}), [k]: v }));

  return (
    <Card data-testid="workscope-card">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <CardTitle className="text-[15px]">Draft work scope &amp; proposal scaffold</CardTitle>
          <Badge variant="outline" className="text-[10px] uppercase tracking-wide" data-testid="workscope-status">
            {(workScope.approval_status || "draft").replace("_", " ")}
          </Badge>
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={save} disabled={!dirty} data-testid="workscope-save">Save</Button>
            {admin && (
              <Button size="sm" onClick={approveToggle} data-testid="workscope-toggle-approval" style={{ background: "hsl(347 72% 22%)" }}>
                {(workScope.approval_status || "draft") === "internally_approved" ? "Revoke approval" : "Internally approve"}
              </Button>
            )}
          </div>
        </div>
        <p className="text-[11px] italic text-muted-foreground mt-1">
          Nothing here is sent automatically. Approval is an internal marker and not a client-facing action.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Summary"    value={st.summary}       onChange={(v) => patch("summary", v)} multiline testid="workscope-summary" />
          <Field label="Objectives" value={st.objectives}    onChange={(v) => patch("objectives", v)} multiline testid="workscope-objectives" />
          <Field label="Assumptions" value={st.assumptions}  onChange={(v) => patch("assumptions", v)} multiline testid="workscope-assumptions" />
          <Field label="Exclusions"  value={st.exclusions}   onChange={(v) => patch("exclusions", v)} multiline testid="workscope-exclusions" />
          <Field label="Dependencies" value={st.dependencies} onChange={(v) => patch("dependencies", v)} multiline testid="workscope-dependencies" />
          <Field label="Timeline note" value={st.timeline_note} onChange={(v) => patch("timeline_note", v)} testid="workscope-timeline" />
          <Field label="Budget note"   value={st.budget_note}   onChange={(v) => patch("budget_note", v)} testid="workscope-budget" />
          <ListField label="Deliverables" value={st.deliverables || []} onChange={(v) => patch("deliverables", v)} testid="workscope-deliverables" />
        </div>
        <div className="pt-3 border-t">
          <div className="text-[12px] uppercase tracking-wide text-muted-foreground mb-2">Proposal scaffold (never auto-sent)</div>
          <Field label="Proposal title" value={st.proposal_title} onChange={(v) => patch("proposal_title", v)} testid="workscope-proposal-title" />
          <Field label="Proposal body (client-facing draft)" value={st.proposal_body} onChange={(v) => patch("proposal_body", v)} multiline rows={6} testid="workscope-proposal-body" />
        </div>
      </CardContent>
    </Card>
  );
}

function LocPrepTab({ trackId, locCase, setLocCase }) {
  const [reason, setReason] = useState("");
  async function create() {
    try {
      const r = await api.post(`/tracks/${trackId}/loc-prep-case`);
      setLocCase(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create LOC case."); }
  }
  async function patch(fields) {
    try {
      const r = await api.patch(`/tracks/${trackId}/loc-prep-case`, fields);
      setLocCase(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save LOC case."); }
  }
  async function setStatus(status) {
    try {
      const r = await api.post(`/tracks/${trackId}/loc-prep-case/status`, { status, reason });
      setLocCase(r.data);
      setReason("");
      toast.success(`Status set: ${LOC_STATUS_LABELS[status] || status}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Status change refused."); }
  }
  function toggleItem(idx) {
    const cl = [...(locCase.checklist || [])];
    cl[idx] = { ...cl[idx], done: !cl[idx].done };
    patch({ checklist: cl });
  }

  if (!locCase) {
    return (
      <Card data-testid="loc-card">
        <CardContent className="p-4 space-y-2">
          <div className="text-[13px]">No LOC preparation case yet.</div>
          <p className="text-[11.5px] italic text-muted-foreground">
            Preparation and evidence tracking only. Kandake does not perform Library of Congress filings.
          </p>
          <Button size="sm" onClick={create} data-testid="loc-create"><Plus className="w-3.5 h-3.5 mr-1.5" />Start LOC preparation</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="loc-card">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-[15px]">LOC / Copyright filing preparation</CardTitle>
          <Badge variant="outline" className="text-[10px] uppercase tracking-wide" data-testid="loc-status">
            {LOC_STATUS_LABELS[locCase.status] || locCase.status}
          </Badge>
          {locCase.attorney_review_required && (
            <Badge variant="outline" className="text-[10px] uppercase tracking-wide"
              style={{ background: "hsl(0 55% 94%)", color: "hsl(0 65% 30%)", borderColor: "hsl(0 55% 72%)" }}>
              Attorney review required
            </Badge>
          )}
        </div>
        <p className="text-[11px] italic text-muted-foreground mt-1">{locCase.disclaimer_text}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Preparation checklist</div>
          <ul className="space-y-1" data-testid="loc-checklist">
            {(locCase.checklist || []).map((c, i) => (
              <li key={c.key} className="flex items-start gap-2 text-[12.5px]" data-testid={`loc-checklist-${c.key}`}>
                <Checkbox checked={!!c.done} onCheckedChange={() => toggleItem(i)} className="mt-0.5" />
                <span className="flex-1">{c.label}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Filing reference number" value={locCase.filing_reference_number} onChange={(v) => patch({ filing_reference_number: v })} testid="loc-ref" />
          <Field label="Filing date"             value={locCase.filing_date}             onChange={(v) => patch({ filing_date: v })} testid="loc-date" />
          <Field label="Evidence reference"       value={locCase.evidence_reference}     onChange={(v) => patch({ evidence_reference: v })} testid="loc-evidence-ref" />
          <Field label="Evidence note"            value={locCase.evidence_note}          onChange={(v) => patch({ evidence_note: v })} testid="loc-evidence-note" />
          <Field label="Human reviewer (email)"   value={locCase.human_reviewer}         onChange={(v) => patch({ human_reviewer: v })} testid="loc-reviewer" />
          <Field label="External filing status"   value={locCase.external_filing_status} onChange={(v) => patch({ external_filing_status: v })} testid="loc-ext-status" />
        </div>
        <div className="flex flex-wrap items-center gap-2 border rounded p-2.5" style={{ background: "hsl(40 55% 96%)" }}>
          <div className="flex items-center gap-2">
            <Switch checked={!!locCase.attorney_review_required} onCheckedChange={(v) => patch({ attorney_review_required: v })} data-testid="loc-attorney-flag" />
            <Label className="text-[12px]">Attorney review required</Label>
          </div>
          <Input value={locCase.attorney_review_reason || ""} onChange={(e) => patch({ attorney_review_reason: e.target.value })} placeholder="Attorney review reason" className="h-7 text-[11.5px] flex-1 min-w-[160px]" data-testid="loc-attorney-reason" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wide">Internal notes</Label>
          <Textarea value={locCase.internal_notes || ""} onChange={(e) => patch({ internal_notes: e.target.value })} rows={3} className="mt-1" data-testid="loc-notes" />
        </div>

        <div className="pt-2 border-t">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Change status</div>
          <div className="flex flex-wrap gap-2 items-center">
            <Select onValueChange={(v) => setStatus(v)}>
              <SelectTrigger className="h-8 text-[12px] w-[280px]" data-testid="loc-status-select"><SelectValue placeholder="Set status…" /></SelectTrigger>
              <SelectContent>
                {LOC_STATUSES.map((s) => (
                  <SelectItem key={s} value={s} disabled={s === locCase.status} data-testid={`loc-status-opt-${s}`}>
                    {LOC_STATUS_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional; required for attorney review)" className="h-8 text-[11.5px] flex-1 min-w-[220px]" data-testid="loc-status-reason" />
          </div>
          <p className="text-[10.5px] mt-1 text-muted-foreground">
            Moving to FILING EVIDENCE RECORDED requires filing reference number, filing date, and evidence.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function ArtworkReviewTab({ trackId, artwork, setArtwork }) {
  const [outcome, setOutcome] = useState("");
  const [reason, setReason] = useState("");
  async function create() {
    try {
      const r = await api.post(`/tracks/${trackId}/artwork-review`);
      setArtwork(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create artwork review."); }
  }
  async function patch(fields) {
    try {
      const r = await api.patch(`/tracks/${trackId}/artwork-review`, fields);
      setArtwork(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
  }
  async function setArtOutcome() {
    if (!outcome) { toast.error("Choose an outcome."); return; }
    try {
      const r = await api.post(`/tracks/${trackId}/artwork-review/outcome`, { outcome, reason });
      setArtwork(r.data);
      setOutcome("");
      setReason("");
      toast.success("Artwork outcome updated.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save outcome."); }
  }

  if (!artwork) {
    return (
      <Card data-testid="artwork-card">
        <CardContent className="p-4 space-y-2">
          <div className="text-[13px]">No artwork review yet.</div>
          <p className="text-[11.5px] italic text-muted-foreground">
            Compliance-support checklist. An &lsquo;internally approved&rsquo; outcome is not a legal clearance determination.
          </p>
          <Button size="sm" onClick={create} data-testid="artwork-create"><Plus className="w-3.5 h-3.5 mr-1.5" />Start artwork review</Button>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card data-testid="artwork-card">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-[15px]">Artwork compliance review</CardTitle>
          <Badge variant="outline" className="text-[10px] uppercase tracking-wide" data-testid="artwork-outcome-current">{artwork.outcome}</Badge>
        </div>
        <p className="text-[11px] italic text-muted-foreground mt-1">{artwork.disclaimer_text}</p>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Release context" value={artwork.release_context} onChange={(v) => patch({ release_context: v })} testid="artwork-release" />
          <div className="flex items-center gap-2 border rounded p-2 mt-4">
            <Switch checked={!!artwork.title_artist_consistency} onCheckedChange={(v) => patch({ title_artist_consistency: v })} data-testid="artwork-consistency" />
            <Label className="text-[12px]">Title / artist consistency checked</Label>
          </div>
          <Field label="Artwork creator" value={artwork.artwork_creator} onChange={(v) => patch({ artwork_creator: v })} testid="artwork-creator" />
          <Field label="Artwork owner"   value={artwork.artwork_owner}   onChange={(v) => patch({ artwork_owner: v })} testid="artwork-owner" />
          <Field label="Permission / licence status" value={artwork.permission_status} onChange={(v) => patch({ permission_status: v })} testid="artwork-permission" />
          <Field label="Third-party concerns"  value={artwork.third_party_concerns}  onChange={(v) => patch({ third_party_concerns: v })} multiline testid="artwork-3p" />
          <div className="flex items-center gap-2 border rounded p-2 mt-4">
            <Switch checked={!!artwork.trademark_brand_flag} onCheckedChange={(v) => patch({ trademark_brand_flag: v })} data-testid="artwork-trademark-flag" />
            <Label className="text-[12px]">Trademark / brand-use flag</Label>
          </div>
          <Field label="Delivery status"     value={artwork.delivery_status} onChange={(v) => patch({ delivery_status: v })} testid="artwork-delivery" />
          <ListField label="Missing elements" value={artwork.missing_elements || []} onChange={(v) => patch({ missing_elements: v })} testid="artwork-missing" />
          <ListField label="Source documentation refs" value={artwork.source_documentation_refs || []} onChange={(v) => patch({ source_documentation_refs: v })} testid="artwork-sources" />
        </div>
        <Field label="Reviewer notes" value={artwork.reviewer_notes} onChange={(v) => patch({ reviewer_notes: v })} multiline rows={3} testid="artwork-notes" />
        <div className="pt-2 border-t">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Set review outcome</div>
          <div className="flex flex-wrap gap-2 items-center">
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger className="h-8 text-[12px] w-[280px]" data-testid="artwork-outcome-select"><SelectValue placeholder="Choose outcome…" /></SelectTrigger>
              <SelectContent>
                {ARTWORK_OUTCOMES.map((o) => (
                  <SelectItem key={o.code} value={o.code} data-testid={`artwork-outcome-opt-${o.code}`}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" className="h-8 text-[11.5px] flex-1 min-w-[220px]" data-testid="artwork-outcome-reason" />
            <Button size="sm" onClick={setArtOutcome} disabled={!outcome} data-testid="artwork-outcome-save" style={{ background: "hsl(347 72% 22%)" }}>Save outcome</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, value, onChange, multiline, rows = 2, testid }) {
  return (
    <div>
      <Label className="text-[10.5px] uppercase tracking-wide">{label}</Label>
      {multiline ? (
        <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} className="mt-1 text-[12px]" data-testid={testid} />
      ) : (
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 h-8 text-[12px]" data-testid={testid} />
      )}
    </div>
  );
}

function ListField({ label, value, onChange, testid }) {
  const [text, setText] = useState((value || []).join("\n"));
  useEffect(() => { setText((value || []).join("\n")); }, [value]);
  return (
    <div>
      <Label className="text-[10.5px] uppercase tracking-wide">{label}</Label>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => onChange(text.split("\n").map((s) => s.trim()).filter(Boolean))}
        rows={3}
        placeholder="One per line"
        className="mt-1 text-[12px]"
        data-testid={testid}
      />
    </div>
  );
}
